import {
  COOKIE_SETUP,
  MAIN_COOKIE_SETUP,
  RIMS_COOKIE_STG_CONSTANTS,
  RIMS_LOCAL_STG_CONSTANTS,
  RTQ_EVENTS,
  RIMS_SESSION_STG_CONSTANTS,
  ROLE_TYPE_ENUM,
  rootDomain,
} from "../constants";
import cookie from "react-cookies";
import eventBus from "./eventBus";
import { v4 as uuid } from "uuid";
import { useConfigStore } from "../store/config";
import { DEFAULT_LANGUAGE_VALUE, TENANT_BRANDING_CACHE_KEY } from "@freyr/freyr-react-common-components";
import { clearCachedBranding } from "./branding";

export function translateStatus(status: any) {
  if (status === "" || status === undefined || status === null) {
    return "";
  } else {
    switch (Number(status)) {
      case 0:
        return "In-Draft";
      case 1:
        return "Active";
      case 2:
        return "Inactive";
      case 3:
        return "Archived";
      default:
        return "In-Draft";
    }
  }
}

export function mappingAdminType(type: string) {
  switch (type) {
    case "user":
      return 0;
    case "role":
      return 1;
    case "group":
      return 2;
    case "acl":
      return 3;
    default:
      return 0;
  }
}

export function uniqueArray(arr: any[], propName?: string) {
  const res = new Map();
  if (propName) {
    return arr.filter((a) => !res.has(a[propName]) && res.set(a[propName], 1));
  } else {
    return arr.filter((a) => !res.has(a) && res.set(a, 1));
  }
}

export function titleCase(str: string) {
  let newStr = str.split(" ");
  for (let i = 0; i < newStr.length; i++) {
    newStr[i] =
      newStr[i].slice(0, 1).toUpperCase() + newStr[i].slice(1).toLowerCase();
  }
  return newStr.join(" ");
}

export function firstWordCase(parameter: string) {
  return parameter.slice(0, 1).toUpperCase() + parameter.slice(1).toLowerCase();
}

export function transferToHump(parameter: string) {
  parameter = parameter.replace(/\s+/g, "");
  return parameter.slice(0, 1).toLowerCase() + parameter.slice(1);
}

export function handleExportByType(
  api: string,
  enqueueSnackbar: any,
  data?: any
) {
  let params = {
    api: api,
    data: data ? data : {},
    enqueueSnackbar: enqueueSnackbar,
  };
  eventBus.emit(RTQ_EVENTS.COMMON.EXPORT_FILE_START, params);
}

export function clearStorageExcepteLoginDomain(config: Record<string, string | boolean | undefined>) {
  // Preserve side-menu preferences (expand/collapse) so they persist across logout/login.
  const preservedMenuStore = localStorage?.getItem(RIMS_LOCAL_STG_CONSTANTS.AC_MENU_STORE);

  localStorage?.clear();
  sessionStorage?.clear();

  if (preservedMenuStore !== null && preservedMenuStore !== undefined) {
    localStorage?.setItem(RIMS_LOCAL_STG_CONSTANTS.AC_MENU_STORE, preservedMenuStore);
  }

  const cookies = cookie?.loadAll();
  const preserveCookieKeys = [RIMS_COOKIE_STG_CONSTANTS.LOGIN_DOMAIN, TENANT_BRANDING_CACHE_KEY];

  Object.keys(cookies)?.forEach((cookieKey) => {
    if (!preserveCookieKeys.includes(cookieKey)) {
      cookie?.remove(cookieKey, config);
    }
  });
}

export function clearAllStorage(config: Record<string, string | boolean | undefined>) {
  clearCachedBranding();
  localStorage?.clear();
  sessionStorage?.clear();
  const cookies = cookie?.loadAll();
  Object.keys(cookies)?.forEach((cookieKey) => {
    cookie?.remove(cookieKey, config);
  });
}

export function logoutFunction(config?: Record<string, string | boolean | undefined>) {
  clearStorageExcepteLoginDomain(config ?? COOKIE_SETUP);
}

export function removeAllCookiesAndLoginDomainInRootDomain() {
  clearAllStorage(COOKIE_SETUP);
  cookie?.remove(RIMS_COOKIE_STG_CONSTANTS.LOGIN_DOMAIN, MAIN_COOKIE_SETUP);
}

export function getURLMainDomain() {
  const splitByDot = window.location.hostname.split(".");
  const length = splitByDot.length;
  const domainName = `${splitByDot[length - 2]}.${splitByDot[length - 1]}`;
  return domainName;
}

const isObject = (target: any) => {
  return (
    (typeof target === "object" || typeof target === "function") &&
    target !== null
  );
};

export function deepClone(target: any, map = new WeakMap()) {
  if (map.get(target)) {
    return target;
  }
  if (isObject(target)) {
    map.set(target, true);
    const cloneTarget: any = Array.isArray(target) ? [] : {};
    for (let prop in target) {
      if (target.hasOwnProperty(prop)) {
        cloneTarget[prop] = deepClone(target[prop], map);
      }
    }
    return cloneTarget;
  } else {
    return target;
  }
}

export function operatorName(name: any) {
  switch (name) {
    case "contains":
      return "Contains";
    case "equals":
      return "Equals";
    case "startsWith":
      return "Starts With";
    case "endsWith":
      return "Ends With";
    case "isEmpty":
      return "is Empty";
    case "isNotEmpty":
      return "is Not Empty";
    default:
      return "";
  }
}

export function handleAPICommonError(err: any) {
  if (
    err?.response?.status == 401 ||
    err?.response?.data?.message === "unauthorized"
  ) {
    return;
  }
}

export function getObjectDataType(data: any) {
  const temp = Object.prototype.toString.call(data);
  const type = temp?.match(/\b\w+\b/g) || [];
  return type.length < 2 ? "Undefined" : type[1];
}

export function isObjectChanged(source: any, comparison: any): any {
  const iterable = (data: any) =>
    ["Object", "Array"].includes(getObjectDataType(data));
  if (getObjectDataType(source) !== getObjectDataType(comparison)) {
    return true;
  }
  const sourceKeys = Object.keys(source);
  const comparisonKeys = Object.keys({ ...source, ...comparison });
  if (sourceKeys.length !== comparisonKeys.length) {
    return true;
  }
  return comparisonKeys.some((key: any) => {
    if (iterable(source[key])) {
      return isObjectChanged(source[key], comparison[key]);
    } else {
      return source[key] !== comparison[key];
    }
  });
}

export const uploadFileSize = (input: any) => {
  let Sys = {
    ie: false,
    firefox: false,
    Chrome: false,
  };
  let filesize = 0;
  const userAgent = navigator.userAgent;

  if (userAgent.indexOf("MSIE") > 0 || userAgent.indexOf("rv:11.0") > 0) {
    Sys.ie = true;
  }
  if (userAgent.indexOf("Firefox") > 0) {
    Sys.firefox = true;
  }
  if (userAgent.indexOf("Chrome") > 0) {
    Sys.Chrome = true;
  }

  if (Sys.firefox || Sys.Chrome) {
    filesize = input.files[0].size;
  } else if (Sys.ie) {
    //@ts-ignore
    const fileobject = new ActiveXObject("Scripting.FileSystemObject");
    const file = fileobject.GetFile(input.value);
    filesize = file.Size;
  }
  return filesize / (1024 * 1024);
};
export function dateSortComparator(a: any, b: any): any {
  return Date.parse(a ?? 0) - Date.parse(b ?? 0);
}

export function dependencyTypeName(type: any) {
  switch (type) {
    case 0:
      return "Users";
    case 1:
      return "User Groups";
    case 2:
      return "Roles";
    case 3:
      return "ACLs";
    case 4:
      return "RTQ";
    case 5:
      return "Question";
    case 6:
      return "Workflow";
    case 7:
      return "Distribution";
    case 8:
      return "Clients";
    default:
      return "";
  }
}

export function getURLDomain() {
  const splitByDot = window.location.hostname.split(".");
  const newArray = splitByDot.slice(1);
  const domainName = newArray.join(".");

  return domainName;
}

/** True when Admin Center is opened on the bridge host (not a silo-prefixed URL). */
export function isBridgeLoginHost(): boolean {
  return getURLDomain() === rootDomain;
}

/** Persist Silo customizedDomain for iframe hosts; clear when Bridge / empty. */
export function persistCurrentTenantCustomizedDomain(
  customizedDomain: string | null | undefined
): void {
  const value = (customizedDomain || "").trim();
  if (value) {
    sessionStorage.setItem(
      RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_CUSTOMIZED_DOMAIN,
      value
    );
  } else {
    sessionStorage.removeItem(
      RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_CUSTOMIZED_DOMAIN
    );
  }
}

/**
 * Host domain for SideMenu iframe modules.
 * Bridge host + silo tenant: `{customizedDomain}.{getURLDomain()}`.
 * Bridge host + bridge tenant, or any silo host login: `getURLDomain()` only
 * (silo URL already includes the prefix — do not double-prefix).
 */
export function getIframeUrlDomain(): string {
  const base = getURLDomain();
  if (!isBridgeLoginHost()) {
    return base;
  }
  const silo = (
    sessionStorage.getItem(
      RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_CUSTOMIZED_DOMAIN
    ) || ""
  ).trim();
  return silo ? `${silo}.${base}` : base;
}

export function redirectToLogin() {
  const portalURL = `https://portal.${getURLDomain()}`;
  const language = useConfigStore.getState()?.language || DEFAULT_LANGUAGE_VALUE;

  window.location.href = `${portalURL}?redirect=${window.location.href}&lang=${language}`;
}

export const getUniqueKey = () => uuid();

export const getAllIds = (tree: any, result: any) => {
  if (!Array.isArray(tree)) return [];
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    result.push(node.id);
    if (Array.isArray(node.children)) {
      getAllIds(node.children, result);
    }
  }
  return result;
};

export const canParseToJson = (str: any) => {
  if (typeof str == "string") {
    try {
      var obj = JSON.parse(str);
      if (typeof obj == "object" && obj) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
};

export const bfsSearch = (graph: any, targetId: string, idProperty: string) => {
  const queue = [...graph];

  while (queue.length > 0) {
    const currNode = queue.shift();
    if (currNode[idProperty] === targetId) {
      return currNode;
    }
    if (currNode.children) {
      queue.push(...currNode.children);
    }
  }
  return []; // Target node not found
};

export function getCookieConfig(maxAge: number) {
  return {
    path: "/",
    domain: getURLDomain(),
    maxAge: maxAge ?? 1,
    secure: true
  }
}

export const generateUUID = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  array[6] = (array[6] & 0x0f) | 0x40;
  array[8] = (array[8] & 0x3f) | 0x80;
  //@ts-ignore
  return [...array].map((b) => ('0' + b.toString(16)).slice(-2)).join('').match(/.{1,8}/g).join('-');
}

export function generateUserInfo(data: any) {
  const tenantList = data.tenantInfoList ?? []
  const ownFreyrTenant = tenantList.filter((item: any) => item.id === '0').length > 0;
  let userInfo = {
    roleTypeList: data.roleTypeList,
    email: data.email,
    tenantId: data.tenantId,
    authMechanism: data.authMechanism,
    username: data.username,
    firstName: data.firstName,
    lastName: data.lastName,
    ownFreyrTenant
  }
  return btoa(encodeURIComponent(JSON.stringify(userInfo)));
}


export const checkRepectKey = (data: any) => {
  const keyCount = {};

  data.forEach((item: any) => {
    //@ts-ignore
    if (!keyCount[item.key]) {
      //@ts-ignore
      keyCount[item.key] = { count: 0, ids: [] };
    }
    //@ts-ignore
    keyCount[item.key].count++;
    //@ts-ignore
    keyCount[item.key].ids.push(item.id);
  });

  const idsArray = [];

  for (const key in keyCount) {
    //@ts-ignore
    if (keyCount[key].count > 1) {
      //@ts-ignore
      idsArray.push(...keyCount[key].ids);
    }
  }
  return idsArray
}

export function mergeData(data1: any, data2: any) {
  return data2.map((item2: any) => {
    const matchingItem = data1.find((item1: any) => item1.id === item2.id);
    if (matchingItem) {
      return {
        ...item2,
        description: matchingItem.description
      };
    }
    return item2;
  });
}

export function checkIsFreyrAdmin() {
  const cookieUserInfo = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_INFO) ?? {};
  let userInfo = {} as any;

  try {
    userInfo = cookieUserInfo ? JSON.parse(decodeURIComponent(window.atob(cookieUserInfo))) : {};
  } catch(error) {
    console.log(error);
  }

  return sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT) === "true" &&
    userInfo.roleTypeList?.includes?.(ROLE_TYPE_ENUM.SUPER_ADMIN);
};

/** Resolves the tenant currently selected in the app (header tenant switcher). */
export function getCurrentTenantId(): string {
  return String(
    sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID) ||
      cookie.load(RIMS_COOKIE_STG_CONSTANTS.AC_TENANT_ID) ||
      cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID) ||
      ""
  ).trim();
}
